import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  Zap,
  Clock,
  Edit2,
  Trash2,
  GripVertical,
  X,
  Calendar as CalendarIcon,
  Copy,
  Eye,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  List,
  CalendarDays,
  Image as ImageIcon,
  Video,
  Check,
  Square,
  CheckSquare,
  MoreVertical,
  ExternalLink,
  Share2,
  Filter,
  Search
} from "lucide-react";
import { FaInstagram, FaFacebook, FaTiktok, FaPinterest } from "react-icons/fa";

const platformColors = {
  facebook: "#1877F2",
  instagram: "#E4405F",
  tiktok: "#000000",
  pinterest: "#E60023"
};

const platformIcons = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  tiktok: FaTiktok,
  pinterest: FaPinterest
};

const statusColors = {
  scheduled: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
  draft: { bg: "bg-yellow-500/20", text: "text-yellow-400", border: "border-yellow-500/30" },
  published: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/30" },
  failed: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  pending: { bg: "bg-purple-500/20", text: "text-purple-400", border: "border-purple-500/30" }
};

export default function CalendarPageV2() {
  // Core state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // View state
  const [viewMode, setViewMode] = useState("month"); // month, week, day
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
  });
  
  // Drag and drop
  const [draggedPost, setDraggedPost] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dragOverTime, setDragOverTime] = useState(null);
  
  // Edit state
  const [editingPost, setEditingPost] = useState(null);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedTime, setSelectedTime] = useState("12:00");
  const [showQuickEdit, setShowQuickEdit] = useState(null);
  const [quickEditCaption, setQuickEditCaption] = useState("");
  
  // Selection for batch operations
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [showBatchActions, setShowBatchActions] = useState(false);
  
  // Modal state
  const [showDateDetailModal, setShowDateDetailModal] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [hoveredPost, setHoveredPost] = useState(null);
  
  // Filter state
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchMonthPosts();
  }, [currentMonth]);

  const fetchMonthPosts = async () => {
    try {
      setLoading(true);
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await apiClient.get(`/posts/calendar/month?month=${month}&year=${year}`);
      setPosts(res.data || []);
    } catch (error) {
      console.error("Calendar fetch error:", error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (filterPlatform !== "all" && post.platform !== filterPlatform) return false;
    if (filterStatus !== "all" && post.status !== filterStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const caption = (post.caption || post.content || "").toLowerCase();
      if (!caption.includes(query)) return false;
    }
    return post.status !== 'archived';
  });

  const getPostsForDate = useCallback((date) => {
    return filteredPosts
      .filter(post => {
        if (!post.scheduled_at) return false;
        const postDate = new Date(post.scheduled_at);
        return postDate.toDateString() === date.toDateString();
      })
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  }, [filteredPosts]);

  // Drag and Drop handlers
  const handleDragStart = (e, post) => {
    setDraggedPost(post);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", post.id);
  };

  const handleDragOver = (e, date, time = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverDate(date.toDateString());
    setDragOverTime(time);
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
    setDragOverTime(null);
  };

  const handleDrop = async (e, targetDate, targetTime = null) => {
    e.preventDefault();
    setDragOverDate(null);
    setDragOverTime(null);
    
    if (!draggedPost) return;
    
    const oldDate = new Date(draggedPost.scheduled_at);
    const newDate = new Date(targetDate);
    
    if (targetTime) {
      const [hours, minutes] = targetTime.split(':').map(Number);
      newDate.setHours(hours, minutes, 0, 0);
    } else {
      newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
    }
    
    try {
      await apiClient.put(`/posts/${draggedPost.id}`, {
        scheduled_at: newDate.toISOString()
      });
      
      setPosts(posts.map(p => 
        p.id === draggedPost.id 
          ? { ...p, scheduled_at: newDate.toISOString() }
          : p
      ));
      
      toast.success(`Moved to ${targetDate.toLocaleDateString()}`);
    } catch (error) {
      console.error("Reschedule error:", error);
      toast.error("Failed to reschedule");
    }
    
    setDraggedPost(null);
  };

  // Reorder posts within same day
  const handleReorderPosts = async (date, reorderedPosts) => {
    // Update display order or scheduled times based on order
    const baseTime = new Date(date);
    baseTime.setHours(9, 0, 0, 0);
    
    const updatedPosts = reorderedPosts.map((post, index) => ({
      ...post,
      scheduled_at: new Date(baseTime.getTime() + index * 3600000).toISOString() // 1 hour apart
    }));
    
    setPosts(posts.map(p => {
      const updated = updatedPosts.find(up => up.id === p.id);
      return updated || p;
    }));
    
    // Persist to backend
    for (const post of updatedPosts) {
      try {
        await apiClient.put(`/posts/${post.id}`, {
          scheduled_at: post.scheduled_at
        });
      } catch (error) {
        console.error("Failed to update post order:", error);
      }
    }
    
    toast.success("Posts reordered");
  };

  // Batch operations
  const togglePostSelection = (postId) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
    setShowBatchActions(newSelected.size > 0);
  };

  const selectAllForDate = (date) => {
    const dayPosts = getPostsForDate(date);
    const allSelected = dayPosts.every(p => selectedPosts.has(p.id));
    
    const newSelected = new Set(selectedPosts);
    if (allSelected) {
      dayPosts.forEach(p => newSelected.delete(p.id));
    } else {
      dayPosts.forEach(p => newSelected.add(p.id));
    }
    setSelectedPosts(newSelected);
    setShowBatchActions(newSelected.size > 0);
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(`Delete ${selectedPosts.size} posts?`)) return;
    
    for (const postId of selectedPosts) {
      try {
        await apiClient.delete(`/posts/${postId}`);
      } catch (error) {
        console.error("Delete error:", error);
      }
    }
    
    setPosts(posts.filter(p => !selectedPosts.has(p.id)));
    setSelectedPosts(new Set());
    setShowBatchActions(false);
    toast.success(`${selectedPosts.size} posts deleted`);
  };

  const handleBatchReschedule = () => {
    if (selectedPosts.size === 0) return;
    // Open batch reschedule modal
    setShowTimeModal(true);
    setEditingPost({ batch: true, ids: Array.from(selectedPosts) });
  };

  // Quick inline edit
  const handleQuickEdit = (post) => {
    setShowQuickEdit(post.id);
    setQuickEditCaption(post.caption || post.content || "");
  };

  const saveQuickEdit = async (postId) => {
    try {
      await apiClient.put(`/posts/${postId}`, {
        caption: quickEditCaption
      });
      
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, caption: quickEditCaption } : p
      ));
      
      toast.success("Caption updated");
    } catch (error) {
      toast.error("Failed to update");
    }
    
    setShowQuickEdit(null);
    setQuickEditCaption("");
  };

  // Time editing
  const handleEditTime = (post) => {
    setEditingPost(post);
    const date = new Date(post.scheduled_at);
    setSelectedTime(`${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`);
    setShowTimeModal(true);
  };

  const handleSaveTime = async () => {
    if (!editingPost) return;
    
    if (editingPost.batch) {
      // Batch reschedule
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      for (const postId of editingPost.ids) {
        const post = posts.find(p => p.id === postId);
        if (post) {
          const date = new Date(post.scheduled_at);
          date.setHours(hours, minutes, 0, 0);
          
          try {
            await apiClient.put(`/posts/${postId}`, {
              scheduled_at: date.toISOString()
            });
            
            setPosts(prev => prev.map(p => 
              p.id === postId ? { ...p, scheduled_at: date.toISOString() } : p
            ));
          } catch (error) {
            console.error("Batch reschedule error:", error);
          }
        }
      }
      
      toast.success(`${editingPost.ids.length} posts rescheduled`);
      setSelectedPosts(new Set());
      setShowBatchActions(false);
    } else {
      const date = new Date(editingPost.scheduled_at);
      const [hours, minutes] = selectedTime.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
      
      try {
        await apiClient.put(`/posts/${editingPost.id}`, {
          scheduled_at: date.toISOString()
        });
        
        setPosts(posts.map(p => 
          p.id === editingPost.id 
            ? { ...p, scheduled_at: date.toISOString() }
            : p
        ));
        
        toast.success("Time updated!");
      } catch (error) {
        toast.error("Failed to update time");
      }
    }
    
    setShowTimeModal(false);
    setEditingPost(null);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    
    try {
      await apiClient.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleCopyCaption = async (post) => {
    const text = `${post.caption || post.content || ""}\n\n${(post.hashtags || []).map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  // Navigation
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handlePrevWeek = () => {
    setCurrentWeekStart(new Date(currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(new Date(currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000));
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(today);
    const day = today.getDay();
    setCurrentWeekStart(new Date(today.getFullYear(), today.getMonth(), today.getDate() - day));
  };

  // Generate calendar data
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const startPadding = firstDay.getDay();
    for (let i = 0; i < startPadding; i++) {
      const prevDate = new Date(year, month, -startPadding + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let i = 6; i <= 22; i++) {
      slots.push(`${String(i).padStart(2, '0')}:00`);
    }
    return slots;
  };

  const calendarDays = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isToday = (date) => date?.toDateString() === new Date().toDateString();
  const isSelected = (date) => date?.toDateString() === selectedDate.toDateString();

  // Platform Icon Component
  const PlatformIcon = ({ platform, size = "sm" }) => {
    const Icon = platformIcons[platform];
    const sizeClass = size === "sm" ? "w-3 h-3" : size === "md" ? "w-4 h-4" : "w-5 h-5";
    return Icon ? <Icon className={sizeClass} style={{ color: platformColors[platform] }} /> : null;
  };

  // Post Card Component for all views
  const PostCard = ({ post, compact = false, showCheckbox = true }) => {
    const postDate = new Date(post.scheduled_at);
    const timeStr = postDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    const isSelected = selectedPosts.has(post.id);
    const status = statusColors[post.status] || statusColors.draft;
    
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        className={`group relative rounded-lg border transition-all cursor-pointer ${
          isSelected ? 'ring-2 ring-viraly-orange' : ''
        } ${status.border} ${compact ? 'p-2' : 'p-3'}`}
        style={{ backgroundColor: `${platformColors[post.platform]}10` }}
        draggable
        onDragStart={(e) => handleDragStart(e, post)}
        onMouseEnter={() => setHoveredPost(post)}
        onMouseLeave={() => setHoveredPost(null)}
        onClick={() => !compact && setExpandedPostId(expandedPostId === post.id ? null : post.id)}
        data-testid={`post-card-${post.id}`}
      >
        {/* Selection checkbox */}
        {showCheckbox && (
          <div 
            className="absolute -top-1 -left-1 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              togglePostSelection(post.id);
            }}
          >
            {isSelected ? (
              <CheckSquare className="w-5 h-5 text-viraly-orange" />
            ) : (
              <Square className="w-5 h-5 text-viraly-muted hover:text-viraly-orange" />
            )}
          </div>
        )}
        
        {/* Drag handle */}
        <GripVertical className="absolute top-1/2 -translate-y-1/2 -left-1 w-3 h-3 text-viraly-muted opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing" />
        
        <div className="flex items-start gap-2">
          {/* Thumbnail preview */}
          {(post.image_url || post.video_url) && !compact && (
            <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-white/5">
              <img 
                src={post.image_url || post.thumbnail_url} 
                alt="" 
                className="w-full h-full object-cover"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          
          {/* Platform icon (only show if no thumbnail or compact) */}
          {(compact || (!post.image_url && !post.video_url)) && (
            <div
              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${platformColors[post.platform]}30` }}
            >
              <PlatformIcon platform={post.platform} size="sm" />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-xs text-viraly-muted">{timeStr}</span>
              {post.video_url && <Video className="w-3 h-3 text-blue-400" />}
              {!post.video_url && post.image_url && <ImageIcon className="w-3 h-3 text-green-400" />}
            </div>
            {!compact && (
              <p className="text-xs text-viraly-light truncate mt-0.5">
                {post.caption?.substring(0, 50) || post.content?.substring(0, 50) || "No caption"}
              </p>
            )}
          </div>
          
          {/* Quick actions */}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditTime(post);
              }}
              className="p-1 hover:bg-white/10 rounded"
              title="Edit time"
            >
              <Clock className="w-3 h-3 text-viraly-muted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyCaption(post);
              }}
              className="p-1 hover:bg-white/10 rounded"
              title="Copy"
            >
              <Copy className="w-3 h-3 text-viraly-muted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePost(post.id);
              }}
              className="p-1 hover:bg-red-500/20 rounded"
              title="Delete"
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </button>
          </div>
        </div>
        
        {/* Status badge */}
        <div className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${status.bg} ${status.text}`}>
          {post.status}
        </div>
      </motion.div>
    );
  };

  // Hover preview tooltip
  const HoverPreview = () => {
    if (!hoveredPost) return null;
    
    return (
      <div className="fixed z-50 pointer-events-none" style={{ 
        left: '50%', 
        top: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        {/* This would be positioned near the cursor in a real implementation */}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold">Content Calendar</h1>
            <p className="text-viraly-muted text-sm mt-1">
              Drag posts to reschedule • Click to expand • Select multiple for batch actions
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setViewMode("month")}
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
                  viewMode === "month" ? 'bg-viraly-orange text-white' : 'text-viraly-muted hover:text-white'
                }`}
                data-testid="view-month-btn"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Month</span>
              </button>
              <button
                onClick={() => setViewMode("week")}
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
                  viewMode === "week" ? 'bg-viraly-orange text-white' : 'text-viraly-muted hover:text-white'
                }`}
                data-testid="view-week-btn"
              >
                <CalendarDays className="w-4 h-4" />
                <span className="hidden sm:inline">Week</span>
              </button>
              <button
                onClick={() => setViewMode("day")}
                className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
                  viewMode === "day" ? 'bg-viraly-orange text-white' : 'text-viraly-muted hover:text-white'
                }`}
                data-testid="view-day-btn"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Day</span>
              </button>
            </div>
            
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                showFilters ? 'bg-viraly-orange text-white' : 'bg-white/5 text-viraly-muted hover:text-white'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>
            
            {/* Today button */}
            <button
              onClick={goToToday}
              className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-viraly-muted hover:text-white transition-colors"
            >
              Today
            </button>
            
            {/* Create button */}
            <Button 
              className="btn-primary"
              onClick={() => window.location.href = '/create'}
              data-testid="create-post-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Create</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mb-4 overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-3 p-4 bg-white/5 rounded-xl">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-viraly-muted" />
                  <input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-viraly-orange focus:outline-none"
                  />
                </div>
                
                {/* Platform filter */}
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-viraly-orange focus:outline-none"
                >
                  <option value="all">All Platforms</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="tiktok">TikTok</option>
                  <option value="pinterest">Pinterest</option>
                </select>
                
                {/* Status filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-viraly-orange focus:outline-none"
                >
                  <option value="all">All Status</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="failed">Failed</option>
                </select>
                
                {/* Clear filters */}
                {(filterPlatform !== "all" || filterStatus !== "all" || searchQuery) && (
                  <button
                    onClick={() => {
                      setFilterPlatform("all");
                      setFilterStatus("all");
                      setSearchQuery("");
                    }}
                    className="px-3 py-2 text-sm text-viraly-orange hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Batch Actions Bar */}
        <AnimatePresence>
          {showBatchActions && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mb-4"
            >
              <div className="flex items-center justify-between p-3 bg-viraly-orange/20 border border-viraly-orange/30 rounded-xl">
                <span className="text-sm font-medium">
                  {selectedPosts.size} post{selectedPosts.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleBatchReschedule}
                    className="btn-secondary text-sm py-1.5"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Reschedule All
                  </Button>
                  <Button
                    onClick={handleBatchDelete}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm py-1.5"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete All
                  </Button>
                  <button
                    onClick={() => {
                      setSelectedPosts(new Set());
                      setShowBatchActions(false);
                    }}
                    className="p-1.5 hover:bg-white/10 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Calendar Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Calendar Area */}
          <div className="lg:col-span-3 card-base p-4 lg:p-6">
            {/* Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={viewMode === "month" ? handlePrevMonth : handlePrevWeek}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="font-heading text-lg lg:text-xl font-bold">
                {viewMode === "month" 
                  ? currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  : viewMode === "week"
                  ? `${currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                }
              </h2>
              <button
                onClick={viewMode === "month" ? handleNextMonth : handleNextWeek}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Month View */}
            {viewMode === "month" && (
              <>
                {/* Week Days Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {weekDays.map(day => (
                    <div key={day} className="text-center text-xs text-viraly-muted font-medium py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map(({ date, isCurrentMonth }, index) => {
                    const dayPosts = getPostsForDate(date);
                    const isDragOver = dragOverDate === date.toDateString();
                    
                    return (
                      <motion.div
                        key={date.toISOString()}
                        className={`min-h-[100px] lg:min-h-[120px] p-1 lg:p-2 rounded-lg border transition-all ${
                          !isCurrentMonth 
                            ? 'opacity-40 bg-white/5 border-white/5' 
                            : isSelected(date)
                            ? 'border-viraly-orange bg-viraly-orange/10'
                            : isDragOver
                            ? 'border-viraly-orange/50 bg-viraly-orange/5 scale-[1.02]'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                        } ${isToday(date) ? 'ring-2 ring-viraly-orange/50' : ''} cursor-pointer`}
                        onClick={() => setSelectedDate(date)}
                        onDragOver={(e) => handleDragOver(e, date)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, date)}
                        data-testid={`calendar-day-${date.getDate()}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs lg:text-sm font-medium ${
                            isToday(date) ? 'text-viraly-orange' : 'text-viraly-muted'
                          }`}>
                            {date.getDate()}
                          </span>
                          {dayPosts.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                selectAllForDate(date);
                              }}
                              className="p-0.5 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100"
                              title="Select all"
                            >
                              <CheckSquare className="w-3 h-3 text-viraly-muted" />
                            </button>
                          )}
                        </div>
                        
                        {/* Post indicators */}
                        <div className="space-y-1 overflow-hidden">
                          {dayPosts.slice(0, 3).map((post) => (
                            <PostCard key={post.id} post={post} compact showCheckbox={false} />
                          ))}
                          {dayPosts.length > 3 && (
                            <div 
                              className="text-[10px] text-viraly-muted px-1 hover:text-viraly-orange cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate(date);
                                setShowDateDetailModal(true);
                              }}
                            >
                              +{dayPosts.length - 3} more
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Week View */}
            {viewMode === "week" && (
              <div className="overflow-x-auto">
                <div className="min-w-[700px]">
                  {/* Week header */}
                  <div className="grid grid-cols-8 gap-1 mb-2">
                    <div className="text-xs text-viraly-muted font-medium py-2"></div>
                    {getWeekDays().map(day => (
                      <div 
                        key={day.toISOString()} 
                        className={`text-center py-2 rounded-lg cursor-pointer transition-colors ${
                          isToday(day) ? 'bg-viraly-orange/20' : 'hover:bg-white/5'
                        } ${isSelected(day) ? 'ring-2 ring-viraly-orange' : ''}`}
                        onClick={() => setSelectedDate(day)}
                      >
                        <div className="text-xs text-viraly-muted">{weekDays[day.getDay()]}</div>
                        <div className={`text-sm font-medium ${isToday(day) ? 'text-viraly-orange' : ''}`}>
                          {day.getDate()}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Time slots */}
                  <div className="max-h-[500px] overflow-y-auto">
                    {getTimeSlots().map(time => (
                      <div key={time} className="grid grid-cols-8 gap-1 border-t border-white/5">
                        <div className="text-xs text-viraly-muted py-2 pr-2 text-right">
                          {time}
                        </div>
                        {getWeekDays().map(day => {
                          const dayPosts = getPostsForDate(day).filter(p => {
                            const postHour = new Date(p.scheduled_at).getHours();
                            return postHour === parseInt(time);
                          });
                          const isDragOver = dragOverDate === day.toDateString() && dragOverTime === time;
                          
                          return (
                            <div
                              key={`${day.toISOString()}-${time}`}
                              className={`min-h-[50px] p-1 rounded transition-all ${
                                isDragOver ? 'bg-viraly-orange/10' : 'hover:bg-white/5'
                              }`}
                              onDragOver={(e) => handleDragOver(e, day, time)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, day, time)}
                            >
                              {dayPosts.map(post => (
                                <PostCard key={post.id} post={post} compact />
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Day View */}
            {viewMode === "day" && (
              <div className="space-y-2">
                {getTimeSlots().map(time => {
                  const dayPosts = getPostsForDate(selectedDate).filter(p => {
                    const postHour = new Date(p.scheduled_at).getHours();
                    return postHour === parseInt(time);
                  });
                  const isDragOver = dragOverDate === selectedDate.toDateString() && dragOverTime === time;
                  
                  return (
                    <div 
                      key={time}
                      className={`flex gap-4 p-3 rounded-lg border transition-all ${
                        isDragOver ? 'border-viraly-orange bg-viraly-orange/5' : 'border-white/10 hover:bg-white/5'
                      }`}
                      onDragOver={(e) => handleDragOver(e, selectedDate, time)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, selectedDate, time)}
                    >
                      <div className="text-sm text-viraly-muted w-16 flex-shrink-0">
                        {time}
                      </div>
                      <div className="flex-1 space-y-2">
                        {dayPosts.length === 0 ? (
                          <div className="text-xs text-viraly-muted/50 py-2">No posts</div>
                        ) : (
                          dayPosts.map(post => (
                            <PostCard key={post.id} post={post} />
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar - Selected Day Detail */}
          <div className="card-base p-4 lg:p-6 lg:sticky lg:top-4 lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-viraly-orange" />
                {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </h3>
              {getPostsForDate(selectedDate).length > 0 && (
                <span className="text-xs text-viraly-muted">
                  {getPostsForDate(selectedDate).length} posts
                </span>
              )}
            </div>
            
            {getPostsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-10 h-10 text-viraly-muted mx-auto mb-3" />
                <p className="text-viraly-muted text-sm">No posts scheduled</p>
                <Button 
                  className="mt-4 btn-primary text-sm"
                  onClick={() => window.location.href = '/create'}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Post
                </Button>
              </div>
            ) : (
              <Reorder.Group 
                axis="y" 
                values={getPostsForDate(selectedDate)} 
                onReorder={(reordered) => handleReorderPosts(selectedDate, reordered)}
                className="space-y-3"
              >
                {getPostsForDate(selectedDate).map((post) => (
                  <Reorder.Item key={post.id} value={post}>
                    <PostCard post={post} />
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            )}
            
            {/* AI Tip - Content-aware posting times */}
            <div className="mt-6 p-3 rounded-xl bg-viraly-orange/10 border border-viraly-orange/20">
              <div className="flex items-start gap-2">
                <Zap className="w-4 h-4 text-viraly-orange flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-viraly-orange">AI Suggested Times</p>
                  <p className="text-xs text-viraly-muted mt-1">
                    {(() => {
                      const dayPosts = getPostsForDate(selectedDate);
                      if (dayPosts.length > 0) {
                        const post = dayPosts[0];
                        const content = post.caption || post.content || "";
                        // Detect content type for timing suggestion
                        const contentLower = content.toLowerCase();
                        if (['hanuman', 'krishna', 'temple', 'prayer', 'spiritual', 'diwali', 'eid'].some(k => contentLower.includes(k))) {
                          return "Spiritual content: 5-7 AM (dawn prayers) or 6-7 PM (evening)";
                        } else if (['business', 'finance', 'corporate', 'real estate'].some(k => contentLower.includes(k))) {
                          return "Business content: 8-10 AM or 2-3 PM (work hours)";
                        } else if (['restaurant', 'food', 'recipe', 'cooking', 'biryani'].some(k => contentLower.includes(k))) {
                          return "Food content: 11 AM-12 PM or 6-7 PM (meal times)";
                        } else if (['fitness', 'gym', 'workout', 'yoga'].some(k => contentLower.includes(k))) {
                          return "Fitness content: 5-7 AM or 5-6 PM (workout hours)";
                        }
                      }
                      return "General: 9-11 AM and 7-9 PM for peak engagement";
                    })()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Time Edit Modal */}
        <AnimatePresence>
          {showTimeModal && editingPost && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowTimeModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-viraly-card rounded-2xl w-full max-w-sm p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-lg font-bold">
                    {editingPost.batch ? `Reschedule ${editingPost.ids.length} Posts` : 'Edit Time'}
                  </h3>
                  <button
                    onClick={() => setShowTimeModal(false)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm text-viraly-muted mb-2">New Time</label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="w-full p-3 rounded-xl bg-white/5 border border-white/10 focus:border-viraly-orange focus:outline-none"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowTimeModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveTime}
                    className="btn-primary flex-1"
                  >
                    Save
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="animate-spin w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full" />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
